/* VYAGO liquid chrome — <vyago-chrome> custom element.
   A single full-screen fragment shader: domain-warped fractal noise ramped
   through a polished-metal gradient, drifting slowly.

   Renders at a reduced internal resolution (the pattern is soft, so it costs
   nothing visually), pauses when scrolled out of view, holds one still frame
   under prefers-reduced-motion, and degrades to the element's CSS background
   when WebGL is unavailable. */
(() => {
  if (customElements.get('vyago-chrome')) return;

  const VERT = `
attribute vec2 p;
void main() { gl_Position = vec4(p, 0.0, 1.0); }
`;
  const FRAG = `
precision highp float;
uniform vec2  u_res;
uniform float u_time;

float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123); }

float noise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
             mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
}

float fbm(vec2 p) {
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 5; i++) { v += a * noise(p); p *= 2.02; a *= 0.5; }
  return v;
}

void main() {
  vec2 uv = (gl_FragCoord.xy - 0.5 * u_res) / max(u_res.x, u_res.y);
  uv *= 2.2;
  uv.y *= 1.55;              // stretch the folds sideways into ribbons

  float t = u_time * 0.04;

  // domain warp, pushed along one axis so the folds run as long sweeping bands
  vec2 q = vec2(fbm(uv + vec2(0.0, t)), fbm(uv * 0.8 + vec2(4.7, -t)));
  vec2 r = vec2(fbm(uv + 2.6 * q + vec2(1.7, 9.2) + 0.55 * t),
                fbm(uv + 3.6 * q + vec2(8.3, 2.8) - 0.45 * t));
  float f = fbm(uv + 2.9 * r);

  // sharp mirrored bands: thin dark cores between wide bright reflections
  float w = f * 4.2 + r.x * 1.3 + q.y * 0.9 + t * 0.7;
  float bands = abs(sin(w * 3.14159));
  bands = 1.0 - bands;
  bands = smoothstep(0.06, 0.92, bands);

  vec3 shadow    = vec3(0.072, 0.079, 0.092);
  vec3 mid       = vec3(0.545, 0.553, 0.572);
  vec3 highlight = vec3(0.925, 0.932, 0.944);

  vec3 col = mix(shadow, mid, smoothstep(0.0, 0.46, bands));
  col = mix(col, highlight, smoothstep(0.48, 0.90, bands));

  // hard specular line where the fold turns over
  float edge = smoothstep(0.86, 0.995, bands);
  col += edge * 0.30;

  // darker seams so the ribbons separate cleanly
  float seam = 1.0 - smoothstep(0.0, 0.14, bands);
  col -= seam * 0.05;

  float d = length(uv * vec2(1.0, 0.64)) * 0.34;
  col *= 1.0 - d * 0.30;

  gl_FragColor = vec4(col, 1.0);
}
`;

  function compile(gl, type, src) {
    const sh = gl.createShader(type);
    gl.shaderSource(sh, src);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) { gl.deleteShader(sh); return null; }
    return sh;
  }

  class VyagoChrome extends HTMLElement {
    connectedCallback() {
      if (this._started) return;
      this._started = true;

      this.scale = parseFloat(this.getAttribute('scale')) || 1;
      /* Render in device pixels, not CSS pixels. The old version ignored the
         display's pixel ratio, so on any retina or scaled screen the canvas
         was stretched and the metal came out soft. A pixel budget keeps the
         cost sane: the shader is five octaves of noise per pixel, so the
         budget is lower on phones, where the element is small anyway. */
      const coarse = matchMedia('(pointer: coarse)').matches;
      this.dprCap = coarse ? 1.5 : 2;
      this.budget = coarse ? 1.8e6 : 4.2e6;

      const canvas = document.createElement('canvas');
      canvas.setAttribute('aria-hidden', 'true');
      canvas.style.cssText = 'display:block;width:100%;height:100%';
      this.appendChild(canvas);
      this._canvas = canvas;

      const gl = canvas.getContext('webgl', { antialias: false, alpha: false, depth: false });
      if (!gl) { this.setAttribute('data-fallback', ''); return; }
      this._gl = gl;

      const vs = compile(gl, gl.VERTEX_SHADER, VERT);
      const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
      if (!vs || !fs) { this.setAttribute('data-fallback', ''); return; }

      const prog = gl.createProgram();
      gl.attachShader(prog, vs); gl.attachShader(prog, fs); gl.linkProgram(prog);
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) { this.setAttribute('data-fallback', ''); return; }
      gl.useProgram(prog);

      const buf = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
      const loc = gl.getAttribLocation(prog, 'p');
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

      this._uRes = gl.getUniformLocation(prog, 'u_res');
      this._uTime = gl.getUniformLocation(prog, 'u_time');

      this._reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this._raf = 0;
      this._visible = true;
      this._start = performance.now();

      this._ro = new ResizeObserver(() => { if (!this._raf) this._raf = requestAnimationFrame(this._frame); });
      this._ro.observe(canvas);

      this._io = new IntersectionObserver(([e]) => {
        this._visible = e.isIntersecting;
        if (this._visible && !this._raf) this._raf = requestAnimationFrame(this._frame);
      }, { rootMargin: '120px' });
      this._io.observe(this);

      this._raf = requestAnimationFrame(this._frame);
    }

    _size() {
      const { _canvas: c, _gl: gl } = this;
      const dpr = Math.min(window.devicePixelRatio || 1, this.dprCap);
      let k = this.scale * dpr;
      const cw = c.clientWidth || 1, ch = c.clientHeight || 1;
      const pixels = cw * ch * k * k;
      if (pixels > this.budget) k *= Math.sqrt(this.budget / pixels);
      const w = Math.max(1, Math.round(cw * k));
      const h = Math.max(1, Math.round(ch * k));
      if (c.width !== w || c.height !== h) { c.width = w; c.height = h; gl.viewport(0, 0, w, h); }
      gl.uniform2f(this._uRes, c.width, c.height);
    }

    _frame = (now) => {
      this._raf = 0;
      if (!this._visible || !this._gl) return;
      this._size();
      this._gl.uniform1f(this._uTime, this._reduced ? 12 : (now - this._start) / 1000);
      this._gl.drawArrays(this._gl.TRIANGLES, 0, 3);
      if (!this._reduced) this._raf = requestAnimationFrame(this._frame);
    };

    disconnectedCallback() {
      cancelAnimationFrame(this._raf);
      this._ro && this._ro.disconnect();
      this._io && this._io.disconnect();
      this._gl && this._gl.getExtension('WEBGL_lose_context')?.loseContext();
      this._started = false;
    }
  }

  customElements.define('vyago-chrome', VyagoChrome);
})();
